import type { SeriesAttributes, SessionAttributes } from '@/server/models'

import type { LATEST_TUTOR_TERMS } from '@/queries/UserQ'
import type { PrimaryInterestV2 } from '@/client/services/primaryInterest/types'
import type { USER_LEVELS } from '@/client/services/progression'
import type { SATInvitationParams } from '@/client/services/satBootcamp'
import type { SuspensionActionContext } from '@/client/services/user'

export const SERIES_COURSE_DOMAIN = 'Series'
export const OTHER_DOMAIN = 'Other'
export const MATH_DOMAIN = 'Math'
export const SCIENCE_DOMAIN = 'Science'
export const TEST_PREP_DOMAIN = 'Test Prep'
export const PILOT_DOMAIN = 'Pilot'

export const GENERAL_COURSE = 'General'
export const PRE_ALGEBRA_COURSE = 'Pre-Algebra'
export const ENRICHMENT_COURSE = 'Enrichment'
export const COMMUNITY_COURSE = 'Community'
export const INDIAN_CURRICULUM_COURSE = 'Indian Curriculum'
export const AP_REVIEW_COURSE = 'AP® Review'
export const SAT_PREP_COURSE = 'SAT® Prep'
export const TRIGONOMETRY_COURSE = 'Trigonometry'
export const SAT_PILOT_COURSE = 'SAT® Pilot'
export const SAT_BOOTCAMP_MATH_COURSE = 'SAT® Bootcamp Math'
export const SAT_BOOTCAMP_READING_COURSE = 'SAT® Bootcamp Reading & Writing'
export const CAW_ESSAY_OFFERING_SLUG = 'caw-essay'
export const CAW_WORKSHOP_ESSAY = 'The Essay'
export const CAW_APPLICATION_OFFERING_SLUG = 'caw-application'
export const CAW_WORKSHOP_APPLICATION = 'The Application'
export const CAW_COLLEGE_LIST_OFFERING_SLUG = 'college-list'
export const CAW_COLLEGE_LIST_WORKSHOP = 'How to Create Your College List'
export const CAW_FINANCIAL_AID_DECODED_OFFERING_SLUG = 'financial-aid-decoded'
export const CAW_FINANCIAL_AID_DECODED_WORKSHOP = 'Financial Aid Decoded'
export const CAW_GETTING_UNSTUCK_ESSAY_OFFERING_SLUG = 'caw-getting-unstuck-essay'
export const CAW_GETTING_UNSTUCK_ESSAY_WORKSHOP = 'Getting Unstuck on Your College Essay'
export const MIDDLE_SCHOOL_MATH_COURSE = 'Middle School Math'
export const COLLEGE_ALGEBRA_COURSE = 'College Algebra'
export const COURSE_SLUG_SAT = 'sat'
export const COURSE_SLUG_SAT_PILOT = 'sat-pilot'
export const COURSE_SLUG_SAT_BOOTCAMP_MATH = 'sat-bootcamp-math'
export const COURSE_SLUG_SAT_BOOTCAMP_READING = 'sat-bootcamp-reading-writing'
export const COURSE_SLUG_COMMUNITY = 'community'
export const COURSE_SLUG_EXPERIMENTAL = 'experimental'
export const COURSE_SLUG_MIDDLE_SCHOOL_MATH = 'middle-school-math'
export const COURSE_SLUG_DIALOGUES = 'dialogues'

export const ALGEBRA2_LEARNING_LABS_POLYNOMIAL_ARITHMETIC_OFFERING_SLUG =
  'ka-learning-labs-alg2-unit1'
export const ALGEBRA2_LEARNING_LABS_LOGARITHMS_OFFERING_SLUG = 'ka-learning-labs-alg2-unit8'
export const ALGEBRA2_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG = 'ka-learning-labs-alg2-unit11'
export const PRE_CALCULUS_LEARNING_LABS_TRIGONOMETRY_OFFERING_SLUG =
  'ka-learning-labs-precalc-unit2'
export const PRE_CALCULUS_LEARNING_LABS_MATRICES_OFFERING_SLUG = 'ka-learning-labs-precalc-unit7'
export const PRE_CALCULUS_LEARNING_LABS_PROBABILITY_AND_COMBINATORICS_OFFERING_SLUG =
  'ka-learning-labs-precalc-unit8'

// Topic constants
export const ENRICHMENT_TOPIC = 'Enrichment'
export const OTHER_TOPICS_TOPIC = 'Other Topics'
export const LIVE_HELP_TOPIC = 'Live Help'
export const DOUBT_CLEARING_TOPIC = 'Doubt Clearing'
export const COURSE_CHALLENGE_TOPIC = 'Course Challenge'
export const SAT_PILOT_MATH_TOPIC = 'SAT Math'
export const SAT_PILOT_READING_TOPIC = 'SAT Reading & Writing'
export const SAT_PREP_MATH_TOPIC = 'SAT Math'
export const SAT_PREP_READING_TOPIC = 'SAT Reading & Writing'
export const SAT_BOOTCAMP_MATH_TOPIC = 'SAT Math'
export const SAT_BOOTCAMP_READING_TOPIC = 'SAT Reading & Writing'
export const SAT_BOOTCAMP_ONBOARDING_TOPIC = 'SAT Bootcamp Onboarding'
export const SAT_BOOTCAMP_OFFICE_HOURS_TOPIC = 'SAT Bootcamp Office Hours'
export const STUDY_SPACES_TOPIC = 'Study Spaces'
export const COMMUNITY_EVENTS_TOPIC = 'Community Events'
export const TUTOR_LEARNING_CIRCLES_TOPIC = 'Tutor Learning Circles'
export const SAL_QA_TOPIC = 'Sal Q&As'
export const FEEDBACK_FOR_SCHOOLHOUSE_TOPIC = 'Feedback for Schoolhouse'
export const COMMUNITY_UPDATES_TOPIC = 'Community Updates'
export const JEE_NEET_TOPIC = 'JEE/NEET Preparation'
export const CISCE_BOARD_TOPIC = 'CISCE Board'
export const COLLEGE_ADVICE_TOPIC = 'College Advice'
export const CBSE_12_TOPIC = 'CBSE Class 12th'
export const CBSE_11_TOPIC = 'CBSE Class 11th'
export const CBSE_10_TOPIC = 'CBSE Class 10th'
export const CBSE_9_TOPIC = 'CBSE Class 9th'
export const PHYSICS_TOPIC = 'Physics'
export const BIOLOGY_TOPIC = 'Biology'
export const CHEMISTRY_TOPIC = 'Chemistry'
export const COMPUTER_SCIENCE_TOPIC = 'Computer Science'
export const AP_PHYSICS_TOPIC = 'AP Physics'
export const AP_CHEMISTRY_TOPIC = 'AP Chemistry'
export const AP_BIOLOGY_TOPIC = 'AP Biology'
export const AP_COMPUTER_SCIENCE_TOPIC = 'AP Computer Science'
export const AP_CALCULUS_TOPIC = 'AP Calculus'
export const AP_STATISTICS_TOPIC = 'AP Statistics'
export const MIDDLE_SCHOOL_MATH_TOPIC = 'Middle School Math'
export const DIALOGUES_TOPIC = 'Dialogues'

// TODO: Do we want to restrict these sessions to only tutors?
export const NEW_TUTOR_ONBOARDING_TOPIC = 'New Tutor Onboarding'

export const DIALOGUE_CLUB_TEMPLATE_SLUG = 'dialogue-club'
export const DIALOGUE_CLUB_FALL_2025_TEMPLATE_SLUG = 'dialogue-club-fall-2025'
const DIALOGUES_CLUB_SUMMER_2025_TEMPLATE_SLUG = 'dialogue-club-summer-2025'
const DIALOGUE_CLUB_SPRING_2024_TEMPLATE_SLUG = 'dialogue-club-semester-2024'
const DIALOGUE_CLUB_4_SESSIONS_TEMPLATE_SLUG = 'dialogue-club-trial-2024'
const DIALOGUE_CLUB_GENERAL_TEMPLATE_SLUG = 'dialogue-club'
const DIALOGUE_CLUB_PRESIDENT_TEMPLATE_SLUG = 'dialogue-president-club'
export const DIALOGUE_EVENT_TEMPLATE_SLUG = 'dialogue-event'

export const DIALOGUE_CLUB_TEMPLATE_SLUGS = [
  DIALOGUE_CLUB_TEMPLATE_SLUG,
  DIALOGUES_CLUB_SUMMER_2025_TEMPLATE_SLUG,
  DIALOGUE_CLUB_SPRING_2024_TEMPLATE_SLUG,
  DIALOGUE_CLUB_4_SESSIONS_TEMPLATE_SLUG,
  DIALOGUE_CLUB_FALL_2025_TEMPLATE_SLUG,
  DIALOGUE_CLUB_GENERAL_TEMPLATE_SLUG,
  DIALOGUE_CLUB_PRESIDENT_TEMPLATE_SLUG,
]

export const DIALOGUES_TEMPLATE_SLUGS = [
  ...DIALOGUE_CLUB_TEMPLATE_SLUGS,
  DIALOGUE_EVENT_TEMPLATE_SLUG,
]

export const SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG = 'sat-bootcamp-onboarding'
export const SAT_BOOTCAMP_TUTOR_SUPPORT_LABS_TEMPLATE_SLUG = 'tutor-support-lab'

export const MATH_COURSE_SLUGS = [
  'pre-algebra',
  'algebra',
  'algebra2',
  'geometry',
  'trigonometry',
  'pre-calculus',
  'calculus',
  'statistics',
]

export const COLLEGE_BOARD_PARTNER_SLUG = 'collegeboard'
export const COLLEGE_BOARD_RECORDING_CONSENT = 'accepted_recording_consent_4_13_22'
export const INTERNAL_STATE_SAT_PILOT_V2 = 'sat_pilot_v2'

export const HONORARY_CERTIFICATION_URL = 'https://schoolhouse.world/'

export const WELCOME_EXPLORE_SUBWORLD_ACKNOWLEDGEMENT = 'welcome-explore-subworld-06-08-23'

export const DIALOGUES_IS_A_PART_OF_ENRICHMENT = 'dialogues:is-a-part-of-enrichment-10-20-24'
export const DIALOGUES_FILTER_BY_DIALOGUES_TOPIC = 'dialogues:filter-by-dialogues-topic-10-20-24'

export const LEAD_LAUNCH = 'lead-launch-04-11-25'

// Each one-time acknowledgement must be unique
export const ONE_TIME_ACKNOWLEDGEMENTS = [
  'accepted-volunteer-hours-06-02-22',
  'accepted-volunteer-hours-12-08-22',
  'dismissed-tutor-summer-camp-06-03-22',
  'dismissed-join-summer-camp-06-09-22',
  'accepted-updated-privacy-policy-08-26-22',
  'dashboard:add-phone-number-11-18-22',
  'dashboard:become-tutor-11-18-22',
  'dashboard:continue-becoming-tutor-11-18-22',
  'dashboard:live-help-11-30-22',
  'accepted-recording-policy-02-22-23',
  'sat-tutor-watched-intro-video-03-07-23',
  'caw:the-essay:07-24:survey:pre-survey',
  // Below SAT acknowledgements are deprecated in favor of the ONE_TIME_ACKNOWLEDGEMENTS_PREFIXES.
  'dashboard:sat-learner-assent-11-18-22',
  'dashboard:sat-learner-request-consent-11-18-22',
  'dashboard:sat-learner-sign-up-11-18-22',
  'dashboard:sat-tutor-progress-11-18-22',
  'dashboard:sat-tutor-assent-11-18-22',
  'dashboard:sat-tutor-request-consent-11-18-22',
  'dashboard:sat-tutor-create-bootcamp-11-18-22',
  'dashboard:portfolio-12-04-23',
  'beta:intro-04-28-23',
  WELCOME_EXPLORE_SUBWORLD_ACKNOWLEDGEMENT,
  'subworlds-onboarding-06-11-23',
  'dashboard:tutor-matching-survey-08-08-23',
  'sat-tutor-publish-bootcamp-after-level-up-08-16-23',
  'dashboard:community-survey-10-11-23',
  'dashboard:algebra-1:ka-bootcamp-12-1-23',
  'dashboard:skill-builder-01-01-24',
  'skill-builder-learner-register-bootcamp-03-28-24',
  'dashboard:community-survey-05-28-24',
  'dashboard:community-survey-11-25-24',
  'dashboard:community-survey-05-26-25',
  'dashboard:journey-launch-06-11-24',
  'caw:the-essay:07-24:survey:pre-survey',
  'caw:the-essay:07-24:survey:session-1',
  'caw:the-essay:07-24:survey:session-2',
  'caw:the-essay:07-24:survey:session-3',
  'caw:the-essay:07-24:survey:session-4',
  'caw:the-essay:07-24:survey:session-5',
  'caw:the-essay:07-24:survey:session-6',
  'caw:the-essay:07-24:survey:session-7',
  'caw:the-essay:07-24:survey:session-8',
  DIALOGUES_IS_A_PART_OF_ENRICHMENT,
  DIALOGUES_FILTER_BY_DIALOGUES_TOPIC,
  'privacy-policy-update-2025-02',
  LEAD_LAUNCH,
] as const

export type OneTimeAcknowledgement = (typeof ONE_TIME_ACKNOWLEDGEMENTS)[number]

export enum SessionRequestRecommendationType {
  RequestPortal = 'request-portal',
  Host = 'host',
}

export const EMPTY_VOLUNTEER_REQUIREMENTS = {
  min_certifications: null,
  min_sessions_hosted: null,
  min_months_tutored: null,
  min_peer_review_level: null,
}

export const VOLUNTEER_ROLE_REQUIREMENT_KEYS = [
  'min_certifications',
  'min_sessions_hosted',
  'min_months_tutored',
  'min_peer_review_level',
] as const

export type VolunteerRoleRequirementKeys = (typeof VOLUNTEER_ROLE_REQUIREMENT_KEYS)[number]

export type VolunteerRoleRequirements = {
  [R in VolunteerRoleRequirementKeys]?: R extends 'min_peer_review_level'
    ? string | null
    : number | null
}

// NOTE: If you add a new volunteer team, you need to add a matching activity type here.
// If you'd like to remove a role from this list, please deprecate it by adding it to
// the deprecated activity types section below (not deleting). This ensures we keep
// badges, etc for old volunteer roles.
const VOLUNTEER_TEAM_ACTIVITY_TYPES = [
  'mentor',
  'certification',
  'blog',
  'policy-and-moderation',
  'social-media',
  'tutoring-operations',
  'user-support',
  // 8/26/25: renamed from `subworld-moderator` to `moderator` since we are removing subworlds
  'moderator',
  // deprecated activity types
  'live-help',
  'onboarding-buddy',
  'india',
  'safety',
  'auditor',
  'sat-onboarding',
  'welcoming-team',
  'community-engagement',
] as const

export const NON_VOLUNTEER_TEAM_VOLUNTEER_ACTIVITY_TYPES = [
  'tutor-training',
  'host-session-prep',
  'host-session',
  'coda-hours',
  'session-audited',
  'onboarding-buddy-session',
  'attend-new-tutor-onboarding-session',
  'attend-sat-bootcamp-onboarding-session',
  'other',
] as const

// NOTE: Update RestrictedPagePotentiallySuspendedData when you change this slug
export const LEARNER_ATTENDANCE_TRAINING_SLUG = 'learner-attendance-training'

export const SAT_BOOTCAMP_ONBOARDING_MAKEUP_TRAINING_SLUG = 'sat-onboarding-makeup'

export const VOLUNTEER_ACTIVITY_TYPES = [
  ...NON_VOLUNTEER_TEAM_VOLUNTEER_ACTIVITY_TYPES,
  ...VOLUNTEER_TEAM_ACTIVITY_TYPES,
] as const

export type VolunteerTeamActivityType = (typeof VOLUNTEER_TEAM_ACTIVITY_TYPES)[number]

export type VolunteerActivityType = (typeof VOLUNTEER_ACTIVITY_TYPES)[number]

export const COMMUNITY_ENGAGEMENT_MODERATOR_ROLE_SLUG = 'moderators-community-engagement'
export const TUTOR_SUPPORT_MODERATOR_ROLE_SLUG = 'moderators-tutor-support'
export const BLOG_STAFF_EDITOR_ROLE_SLUG = 'blog-staff-editor'
export const DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG = 'dialogues-club-president'

export const VOLUNTEER_ROLE_SLUGS = [
  COMMUNITY_ENGAGEMENT_MODERATOR_ROLE_SLUG,
  TUTOR_SUPPORT_MODERATOR_ROLE_SLUG,
  BLOG_STAFF_EDITOR_ROLE_SLUG,
  DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG,
] as const

export type VolunteerRoleSlug = (typeof VOLUNTEER_ROLE_SLUGS)[number]

export type Badge = {
  title: string
  subtitle: string
  progress: number
  limit: number
}
export enum CertificationRequestType {
  Honorary = 'honorary',
  Video = 'video',
}

export enum SessionMentorStatus {
  None = 'none',
  NeedsMentor = 'needs-mentor',
  InProgress = 'in-progress',
  Resolved = 'resolved',
}

export type SessionMentorAction = {
  timestamp: string
  status: SessionMentorStatus
  userId: number
  userName: string
  comment: string
  adminMessageId?: number
}

export enum CertificationStatus {
  None = 0,
  Pending = 1,
  Approved = 2,
  NotReadyForReview = 3,
}

export enum TopicType {
  unit = 'unit',
  challenge = 'challenge',
  qa = 'qa',
  other = 'other',
  ap = 'ap',
}

export const TOPIC_TYPE_TO_LABEL: Record<TopicType, string> = {
  [TopicType.unit]: 'Unit Test',
  [TopicType.challenge]: 'Course Challenge',
  [TopicType.qa]: 'Q&A',
  [TopicType.other]: 'Other',
  [TopicType.ap]: 'AP Test',
}

export const AVATAR_TYPES = ['photo', 'avatar'] as const

type AvatarType = (typeof AVATAR_TYPES)[number]

export type Avatar = {
  type: AvatarType
  url?: string | null | undefined
  avatar?: Record<string, string> | null | undefined
}

export type ProgressionLevel = (typeof USER_LEVELS)[number] | null

export enum SessionRequestStatus {
  Unanswered = 'unanswered',
  InProgress = 'in-progress',
  Closed = 'closed',
}

export type ViewpointSettings = {
  question: string
  viewpoint_a: string
  viewpoint_b: string
}

export type SessionSafetyRiskAutomated = 'low' | 'medium' | 'high'

export type SessionInternalState = {
  slack_channel_id?: string | null
  transcript_has_uptake_data?: boolean
  gating_group?: GatingGroup
  viewpoint_settings?: ViewpointSettings | null
}

export type SeriesRegistrationInternalState = {
  bootcamp_nps?: number | null
  source?: string
  pre_survey_completed_at?: string
  post_survey_completed_at?: string
}

export type SeriesHostInternalState = {
  pre_survey_completed_at?: string
  post_survey_completed_at?: string
}

export type SessionRegistrationType = 'session' | 'series' | 'series-dropin'

export const VIEWPOINTS = ['a', 'neutral', 'b'] as const

export type Viewpoint = (typeof VIEWPOINTS)[number]

export type SessionRegistrationInternalState = {
  session_survey_completed_at?: string
  viewpoint?: Viewpoint
}

export type SeriesReservationInternalState = {
  source?: string
}

export type SessionChangelogEntry =
  | {
      timestamp: string
      before: Partial<Record<keyof SessionAttributes, string>>
      after: Partial<Record<keyof SessionAttributes, string>>
      reason: string
      type: 'update'
    }
  | {
      timestamp: string
      reason: string
      type: 'delete'
    }

export type SeriesChangelogEntry = {
  type: 'update'
  timestamp: string
  before: Partial<Record<keyof SeriesAttributes, string>>
  after: Partial<Record<keyof SeriesAttributes, string>>
  sessionsRemovedIds: number[]
  sessionsAddedIds: number[]
  sessionsUpdatedIds: number[]
  reason: string
}

export type SeriesInternalState = {
  sat_bootcamp_score_band?: SATScoreBandName
  research_group?: ResearchGroup
  should_include_uptake?: boolean
  disable_updates?: boolean
  gating_group?: GatingGroup
}

export type SessionRequestActivity = {
  userId: number
  timestamp: Date
  newStatus?: SessionRequestStatus
  newPriority?: boolean
  reason: string
}

export const SESSION_ATTENDANCE_POLICIES = ['optional', 'required'] as const
export type SessionAttendancePolicy = (typeof SESSION_ATTENDANCE_POLICIES)[number]

export enum SeriesEnrollmentStyle {
  Relaxed = 'relaxed',
  Committed = 'committed',
}

export type SeriesClosedReason = 'tutor' | 'tutor-privileges-revoked' | 'user-deleted' | 'admin'

export enum SeriesWithdrawalSource {
  Learner = 'learner',
  Tutor = 'tutor',
  Auto = 'auto',
  TutorAllowedToRejoin = 'tutor-allowed-to-rejoin',
}

export enum UserStoryRole {
  Learner = 'learner',
  Tutor = 'tutor',
}

export enum PrimaryTrainingType {
  TUTOR = 'tutor',
  UNSUSPEND = 'unsuspend',
}

export enum TutorSecondaryTrainingType {
  SAT = 'sat',
  SKILL_BUILDER = 'skill-builder',
  ONBOARDING = 'onboarding',
  ENRICHMENT = 'enrichment',
  LIVE_HELP = 'live-help',
  SAT_BOOTCAMP = 'sat-bootcamp',
  COLLEGE_ADMISSIONS_WORKSHOP = 'college-admissions-workshop',
  SAT_BOOTCAMP_ONBOARDING = 'sat-bootcamp-onboarding',
  LEARNING_LABS = 'learning-labs',
}

export enum UnsuspendSecondaryTrainingType {
  LEARNER_ATTENDANCE = 'learner-attendance',
}

export const SECONDARY_TRAINING_TYPES = {
  ...TutorSecondaryTrainingType,
  ...UnsuspendSecondaryTrainingType,
}

export type SecondaryTrainingType = TutorSecondaryTrainingType | UnsuspendSecondaryTrainingType

export enum TutorTrainingSubmissionAssignmentPriority {
  REQUIRED = 1,
  HIGHLY_RECOMMENDED = 2,
}

export const AOPS_STATUSES = ['verified', 'rejected', 'unreviewed'] as const
export type AopsStatus = (typeof AOPS_STATUSES)[number]

export type AopsState = {
  username: string
  status: AopsStatus
  public: boolean
}

export type SATPilotTopic = 'math' | 'reading'

export type SATPilotTutorInterventionArm = '1' | '2' | '3' | '4'

export type SATPilotLearnerInterventionArm = '1' | '2' | '3' | '4' | '5'

type SATPilotContactInfo = {
  email_address: string
  first_name: string
  last_name: string
}

export type SATPilotTutorChildDetails = {
  type: 'tutor'
  name: string
  college_board_id: string
  eligible_topics: SATPilotTopic[]
  contact_info: SATPilotContactInfo | null
  [COLLEGE_BOARD_RECORDING_CONSENT]?: boolean | null
  form_responses: Record<string, unknown> | null
}

export type SATPilotLearnerChildDetails = {
  type: 'learner'
  name: string
  college_board_id: string
  contact_info: SATPilotContactInfo | null
  [COLLEGE_BOARD_RECORDING_CONSENT]?: boolean | null
  form_responses: Record<string, unknown> | null
  math_score_band: SATScoreBand | null
  reading_score_band: SATScoreBand | null
}

export type SATPilotChildDetails = SATPilotTutorChildDetails | SATPilotLearnerChildDetails

export type SATPilotParentState = {
  type: 'parent'
  [COLLEGE_BOARD_RECORDING_CONSENT]?: boolean | null
  children: SATPilotChildDetails[]
}

export type SATPilotTutorParticipantState = {
  type: 'tutor'
  college_board_id: string
  parent_user_id?: number | null
  eligible_topics: SATPilotTopic[]
  [COLLEGE_BOARD_RECORDING_CONSENT]?: boolean | null
  form_responses: Record<string, unknown> | null
  show_weekly_prioritization_task?: boolean
  tutor_intervention_arm?: SATPilotTutorInterventionArm
}

export type SATPilotLearnerParticipantState = {
  type: 'learner'
  college_board_id: string
  parent_user_id: number | null
  parent_email_address: string | null
  [COLLEGE_BOARD_RECORDING_CONSENT]?: boolean | null
  form_responses: Record<string, unknown> | null
  math_score_band: SATScoreBand | null
  reading_score_band: SATScoreBand | null
  parent_form_responses?: Record<string, unknown> | null
  learner_intervention_arm?: SATPilotLearnerInterventionArm
}

export type SATPilotParticipantState =
  | SATPilotTutorParticipantState
  | SATPilotLearnerParticipantState

export type SATPilotProspectiveTutorState = {
  type: 'prospective-tutor'
  parent_contact_info: SATPilotContactInfo
}

export type SATPilotProspectiveLearnerState = {
  type: 'prospective-learner'
  college_board_id: string
  parent_contact_info: SATPilotContactInfo
  math_score_band: SATScoreBand | null
  reading_score_band: SATScoreBand | null
}

export type SATPilotState =
  | SATPilotParticipantState
  | SATPilotParentState
  | SATPilotProspectiveTutorState
  | SATPilotProspectiveLearnerState

export type SATPilotUserType = SATPilotState['type']

// There is no RCT running, which is why this is empty.
export const ACTIVE_RCT_RESEARCH_GROUPS: string[] = []

export type ActiveRCTResearchGroup = (typeof ACTIVE_RCT_RESEARCH_GROUPS)[number]

export const SAT_RESEARCH_GROUPS = [
  'may-2023-sat-bootcamp-rct-math',
  'may-2023-sat-bootcamp-rct-reading',
  'june-2023-sat-bootcamp-rct-math',
  'june-2023-sat-bootcamp-rct-reading',
  'august-2024-sat-bootcamp-rct',
  'december-2024-sat-bootcamp-rct-zinc',
  'december-2024-sat-bootcamp-rct-text-structure',
  'december-2024-sat-bootcamp-rct-control',
  ...ACTIVE_RCT_RESEARCH_GROUPS,
] as const
export type SATResearchGroup = (typeof SAT_RESEARCH_GROUPS)[number]

export const RESEARCH_GROUPS = [
  'lindbergh',
  'kipp',
  ...SAT_RESEARCH_GROUPS,
  'math-matching',
  'sat-math-foundations',
] as const
export type ResearchGroup = (typeof RESEARCH_GROUPS)[number]

export const RESEARCH_GROUP_TO_LABEL: Record<ResearchGroup, string> = {
  lindbergh: 'LEVI Pilot: Lindbergh MS',
  kipp: 'LEVI Pilot: KIPP MS',
  'may-2023-sat-bootcamp-rct-math': 'May 2023 SAT Bootcamp RCT - Math',
  'may-2023-sat-bootcamp-rct-reading': 'May 2023 SAT Bootcamp RCT - Reading & Writing',
  'june-2023-sat-bootcamp-rct-math': 'June 2023 SAT Bootcamp RCT - Math',
  'june-2023-sat-bootcamp-rct-reading': 'June 2023 SAT Bootcamp RCT - Reading & Writing',
  'august-2024-sat-bootcamp-rct': 'August 2024 SAT Bootcamp RCT',
  'math-matching': 'Math Matching',
  'sat-math-foundations': 'SAT Math Foundations',
  'december-2024-sat-bootcamp-rct-zinc':
    'December 2024 SAT Bootcamp Curriculum Experiment RCT: Zinc Arm',
  'december-2024-sat-bootcamp-rct-text-structure':
    'December 2024 SAT Bootcamp Curriculum Experiment RCT: Text Structure Arm',
  'december-2024-sat-bootcamp-rct-control':
    'December 2024 SAT Bootcamp Curriculum Experiment RCT: Control Arm',
}

export const GATING_GROUP_TO_LABEL: Record<GatingGroup, string> = {
  '10-25-volunteer-only': 'Volunteer Only Game Night',
  'november-college-sprint': 'November College Sprint',
  '01-23-sat-math-skills-builder': 'January 2024 SAT Math Skills Builder',
  '03-2024-sat-math-skills-builder': 'March 2024 SAT Math Skills Builder',
  fallback: 'Too lazy to make a new group? Just use this',
  '2024-03-tfa-bootcamp': 'March 2024 TFA SAT Bootcamp',
  '2024-03-ka-algebra2': 'March 2024 KA Algebra 2 Linkage',
  '2024-08-tfa-seogaville': '2024 TFA Seagoville High School',
  '2024-08-tfa-travis': '2024 TFA Travis Early College High School',
}

export const GATING_GROUPS = [
  'november-college-sprint',
  '10-25-volunteer-only',
  'fallback',
  '01-23-sat-math-skills-builder',
  '03-2024-sat-math-skills-builder',
  '2024-03-tfa-bootcamp',
  '2024-03-ka-algebra2',
  '2024-08-tfa-seogaville',
  '2024-08-tfa-travis',
] as const
export type GatingGroup = (typeof GATING_GROUPS)[number]

export const LIVE_HELP_TUTOR_EXCLUDED_TOPIC_IDS_KEY = 'live_help_tutor_excluded_topic_ids'
export const ARCHIVED_PERSONAL_DATA_KEY = 'archived_personal_data'

export type BetaApplication = {
  tutor_subworld_slugs: string[]
  learn_subworld_slugs: string[]
  why_use_schoolhouse: string
  created_at: string
  approved_at: string | null
}

export type MigratedDialoguesData = {
  legacySessions: number
  selfReflections: { comment: string; date: string }[]
  peerReflections: { comment: string; date: string }[]
  strengths: { label: string; count: number }[]
  topics: string[]
}

type SATClosedRegistrationExperimentV2Data = {
  satDate: string
  submittedAt: string
}

export type UserInternalState = {
  aops?: AopsState
  khan_academy?: KhanAcademyState
  migrated_num_sessions?: number
  migrated_num_helpful?: number
  migrated_num_super_helpful?: number
  college_board_pilot_id?: number
  key_club_district?: string
  key_club_club?: string
  // Store the Slack join date as an ISO string
  migrated_slack_join_date?: string
  tree_receipt_url?: string
  dismissed_sat_pilot_v2_invitation?: boolean
  research_group?: ResearchGroup
  [INTERNAL_STATE_SAT_PILOT_V2]?: SATPilotState
  [LIVE_HELP_TUTOR_EXCLUDED_TOPIC_IDS_KEY]?: number[]
  subscriptions?: string[]
  completed_new_onboarding_june_23?: boolean
  existing_user_joined_subworlds_june_23?: boolean
  sat_invitation_params?: SATInvitationParams | null
  referred_series_id?: number
  marketing_source?: string
  primary_interest_v2?: PrimaryInterestV2
  migrated_dialogues_data?: MigratedDialoguesData
  [LATEST_TUTOR_TERMS]?: boolean
  sat_closed_registration_experiment_v2_data?: SATClosedRegistrationExperimentV2Data

  /** @deprecated this field is no longer used, as all users have been transitioned to subworlds */
  default_to_beta?: boolean
  /** @deprecated this field is no longer used, as all users have been transitioned to subworlds */
  beta_application?: BetaApplication
}

export type ReferralSourceDetails = {
  sat_pilot_v2_email?: string
  sat_pilot_v2?: SATPilotState
  sat_bootcamp_mar_2024_counselor_email?: string
  sat_bootcamp_mar_2024_teacher_email?: string
}

export type PastEmail = {
  email_address: string
  removed_at: string
}

export type SATScoreBand = 'low' | 'high'

export const SAT_SCORE_BAND_NAME_400_490 = '400 - 490'
export const SAT_SCORE_BAND_NAME_500_590 = '500 - 590'
export const SAT_SCORE_BAND_NAME_600_690 = '600 - 690'

export const SAT_SCORE_BAND_NAMES = [
  SAT_SCORE_BAND_NAME_400_490,
  SAT_SCORE_BAND_NAME_500_590,
  SAT_SCORE_BAND_NAME_600_690,
] as const
export type SATScoreBandName = (typeof SAT_SCORE_BAND_NAMES)[number]

export const SAT_SCORE_BANDS: {
  name: SATScoreBandName
  mathWeight: number
  readingWeight: number
  belowThreshold: boolean
}[] = [
  {
    name: SAT_SCORE_BAND_NAME_400_490,
    mathWeight: 25,
    readingWeight: 20,
    belowThreshold: true,
  },
  {
    name: SAT_SCORE_BAND_NAME_500_590,
    mathWeight: 43,
    readingWeight: 40,
    belowThreshold: true,
  },
  {
    name: SAT_SCORE_BAND_NAME_600_690,
    mathWeight: 32,
    readingWeight: 40,
    belowThreshold: false,
  },
]

export type KhanAcademyState = {
  username: string
}

export const SESSION_FEEDBACK_TYPE_TUTOR = 'tutor-feedback'
export const SESSION_FEEDBACK_TYPE_DIALOGUES = 'dialogues-feedback'
export type SessionFeedbackType =
  | typeof SESSION_FEEDBACK_TYPE_TUTOR
  | typeof SESSION_FEEDBACK_TYPE_DIALOGUES

// Note: Keep this enum aligned with the typeguard in `server/api/methods/session.validation.ts`.
export enum TutorSessionFeedbackRating {
  NoOpinion = 0,
  NotHelpful = 1,
  Helpful = 2,
  SuperHelpful = 3,
}

export enum DialoguesSessionFeedbackRating {
  NoOpinion = 0,
  AgreedStruggledToExplore = 1,
  AgreedAndExplored = 2,
  DifferingViewsOnTopic = 3,
}

export type SessionFeedbackRatingType = TutorSessionFeedbackRating | DialoguesSessionFeedbackRating

export enum UserConnectionService {
  GoogleCalendarSessions = 'google-calendar',
}

export enum UserConnectionType {
  OAuth2 = 'oauth2',
}

export enum SessionAttendeeRole {
  Tutor = 'tutor',
  Learner = 'learner',
  /** @deprecated in favor of Welcoming Team */ OnboardingBuddy = 'onboarding_buddy',
}

export type HostRole = 'main' | 'alternative'

export type HostDeletedSource = 'self' | 'host' | 'tutor-privileges-revoked' | 'user-deleted'

export type LiveHelpRegistrationDetails = {
  topicId: number
  description: string | null
  registeredAt: string
}

export type SessionRegistrationDetails = Record<string, unknown> & {
  live_help_details?: LiveHelpRegistrationDetails[]
}

export type SATUserType = 'learner' | 'tutor'

export const SAT_USER_SOURCES = ['college-board', 'referral', 'unknown'] as const

export type SATUserSource = (typeof SAT_USER_SOURCES)[number]

export type SATUserInternalState = {
  research_group?: ResearchGroup
  psat_eligible?: boolean
  // This referral code just uses sat_user, because parents don't necessarily have an associated user
  sat_referral_code?: string
  // This is our normal user referral code
  referral_code?: string
  // Which month (or whether unsure about it) is this user taking their SAT
  test_schedule?: string
  // Whether an admin has waived the Senior Tutor requirement that they must have led a 'highly engaging' SAT Bootcamp
  engaging_bootcamp_requirement_waived_at?: Date | null
  last_completed_tutor_refresher_training_at?: Date | null
  // additional one-pager for tutors teaching 1 on 1 bootcamps in March 2025
  completed_03_25_one_on_one_training_at?: Date | null
  counselor_email?: string | null
}

export const SAT_TEST_USER_TASK_KEYS = ['sat-training-curriculum'] as const
type SATTestUserTaskKeys = (typeof SAT_TEST_USER_TASK_KEYS)[number]

export type SATTestUserTaskCompletions = {
  [K in SATTestUserTaskKeys]?: Date
}

export const MEMBER_LEVEL = 'member'
export const TUTOR_LEVEL = 'tutor'
export const SENIOR_TUTOR_LEVEL = 'senior-tutor'
export const COACH_LEVEL = 'coach'
export const MODERATOR_LEVEL = 'moderator'
export const WORLD_LEVELS = [
  MEMBER_LEVEL,
  TUTOR_LEVEL,
  SENIOR_TUTOR_LEVEL,
  COACH_LEVEL,
  MODERATOR_LEVEL,
] as const

export type WorldLevel = (typeof WORLD_LEVELS)[number]

export type EnactTutorPrivilegeSource =
  | 'admin-tutor-privileges-enacted'
  | 'tutor-privileges-accepted'
  | 'subworld-level-changed'

export type AgeAssuranceSource =
  | 'age-assurance-flow'
  | 'admin-age-assurance'
  | 'college-board-referral'

export type UserRoleAssignedSource =
  | 'backfill'
  | 'manual'
  // | 'create-profile' // <== deprecated, used to exist but not used anymore.
  // We want to have *some* common sources between add role and remove role, so
  // 'sync-profile' is that source.
  | 'sync-profile'
  | 'sync-experiment'
  | 'tutor-privileges-revoked'
  | 'sat-bootcamp-assent'
  | 'sat-bootcamp-consent'
  | 'tutorathon'
  | 'advanced-auditor-level-change'
  | `suspension-${SuspensionActionContext}`
  | 'script'
  | 'worlds-beta-admitted'
  | 'revoke-tutor-privilege-detected-issue-action'
  | 'fellowship-application'
  | 'sat-bootcamp-research-recording-opt-out'
  | EnactTutorPrivilegeSource
  | 'volunteer-role-assigned'
  | 'resource-allocation'
  | 'cron-job'
  | AgeAssuranceSource

export type UserRoleRemovedSource =
  | 'manual'
  | 'sync-profile'
  | 'sync-experiment'
  | 'tutor-privileges-revoked'
  | 'admin-tutor-privileges-enacted'
  | 'sat-bootcamp-consent'
  | `suspension-${SuspensionActionContext}`
  | 'script'
  | 'sat-bootcamp-research-recording-opt-out'
  | 'revoke-tutor-privilege-detected-issue-action'
  | 'volunteer-role-removed'
  | 'resource-allocation'
  | 'cron-job'
  | 'age-update-request'
  | AgeAssuranceSource

export const MATH_MATCHING_GRADES = ['A', 'B', 'C', 'D', 'F']
export type Grade = (typeof MATH_MATCHING_GRADES)[number]

export type MathMatchingExperiment = {
  min_learner_grade_bucket?: Grade
  max_learner_grade_bucket?: Grade
  learner_user_ids?: number[]
}

export const TOPICS_TO_OMIT_FROM_PEER_REVIEWS = [
  SAT_BOOTCAMP_ONBOARDING_TOPIC,
  MIDDLE_SCHOOL_MATH_TOPIC,
]

// Only tutors are allowed to register for these series
export const SAT_BOOTCAMP_TUTOR_ONLY_SERIES_TEMPLATE_SLUGS = [
  SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG,
  SAT_BOOTCAMP_TUTOR_SUPPORT_LABS_TEMPLATE_SLUG,
]
export type SatBootcampTutorOnlySeriesTemplateSlug =
  (typeof SAT_BOOTCAMP_TUTOR_ONLY_SERIES_TEMPLATE_SLUGS)[number]

export enum PushNotificationSubscriptionProvider {
  Firebase = 'fcm',
}

export const ExcusedAbsenceSources = ['learner', 'tutor', 'tutor-reschedule'] as const
export type ExcusedAbsenceSource = (typeof ExcusedAbsenceSources)[number]

export const DELETED_USER_FIRST_NAME = 'DELETED USER'
export const DELETED_USER_BIO = 'This user has been deleted.'
export const DELETED_USER_LAST_NAME = ''

export enum BackgroundCheckPartner {
  Checkr = 'checkr',
}

export enum BackgroundCheckStatus {
  // Candidate created and they got an email from Checkr to submit their data
  requested = 'requested',
  // Candidate submitted their data but waiting for Checkr to complete their process
  submitted = 'submitted',
  // Checkr completed their process and we are found a flag, we are reviewing it
  'in-review' = 'in-review',
  completed = 'completed',
  cancelled = 'cancelled',
}

export const CONSENT_USER_TYPES = ['learner', 'tutor'] as const
export type ConsentUserType = (typeof CONSENT_USER_TYPES)[number]

// Remember to update the default value in database if you make changes here
export enum UserUploadType {
  SIMPLE = 'simple',
  MULTIPART = 'multipart',
}

// See this thread for details behind threshold selection:
// https://schoolhouseproduct.slack.com/archives/C02TEC3TPDG/p1693289822667479
export const TARGET_USER_INCOME_THRESHOLD = 75286

// TODO: Fix this proper. Moved this out of client/services/programs to prevent a recursive import cycle with client/services/url
export const SAT_BOOTCAMP_PROGRAM_SLUG = 'sat-bootcamp'

export const USERNAME_AI_GUIDE = 'ai-guide'

export const ONE_ON_ONE_PILOT_SERIES_TEMPLATE_SLUG = 'one-on-one'
